import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography, Chip, Divider, Tooltip, IconButton } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DoneIcon from '@mui/icons-material/Done';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import ReactMarkdown from 'react-markdown';
import NeoFeedSourcesAudit from './NeoFeedSourcesAudit';
import { PRIMARY, formatDate } from './sharedUi';

/**
 * Card de uma matéria gerada, exibido inline na timeline do chat único
 * (substitui o antigo `VersionCard` resumido + a coluna de preview
 * separada). Mostra título, subtítulo, metadados, corpo em markdown
 * completo (sem truncamento — o scroll do feed acomoda o conteúdo) e as
 * fontes NeoFeed usadas nesta versão.
 *
 * @param {{
 *   version: object;
 *   getVersionKey: (version: object) => string | number | null;
 *   isLatest?: boolean;
 * }} props
 */
export default function TimelineArticleCard({ version, getVersionKey, isLatest = false }) {
    const [copied, setCopied] = useState(false);
    const copyTimerRef = useRef(null);

    useEffect(() => () => clearTimeout(copyTimerRef.current), []);

    const handleCopy = useCallback(() => {
        const lines = [
            `Título: ${version.title ?? ''}`,
            '',
            `Subtítulo: ${version.subtitle ?? ''}`,
            '',
            `Artigo: ${version.article ?? ''}`,
            '',
            `Autor: ${version.author ?? ''}`,
            '',
            `Criado em: ${formatDate(version.written_at)}`,
        ];
        navigator.clipboard.writeText(lines.join('\n')).then(() => {
            setCopied(true);
            clearTimeout(copyTimerRef.current);
            copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
        });
    }, [version]);

    return (
        <Box sx={{
            alignSelf: 'flex-start',
            width: '100%',
            flexShrink: 0,
            border: '1px solid #eee',
            borderRadius: '4px 14px 14px 14px',
            backgroundColor: '#fff',
            overflow: 'hidden',
        }}>
            {/* Cabeçalho do card */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                pt: 1.6,
                pb: version.subtitle ? 0 : 1,
            }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a', lineHeight: 1.35, fontSize: '1.05rem' }}>
                        {version.title}
                    </Typography>
                </Box>
                {isLatest && (
                    <Chip
                        label="Recente"
                        size="small"
                        sx={{ height: 19, fontSize: '0.62rem', backgroundColor: `${PRIMARY}15`, color: PRIMARY, fontWeight: 700, flexShrink: 0 }}
                    />
                )}
                {version.neofeed_sources?.length > 0 && (
                    <Tooltip title={`${version.neofeed_sources.length} fonte(s) NeoFeed usada(s) nesta versão`}>
                        <TravelExploreIcon sx={{ fontSize: 17, color: PRIMARY, flexShrink: 0 }} />
                    </Tooltip>
                )}
                <Tooltip title={copied ? 'Copiado!' : 'Copiar matéria'}>
                    <IconButton
                        size="small"
                        onClick={handleCopy}
                        sx={{
                            flexShrink: 0,
                            color: copied ? '#2e7d32' : '#aaa',
                            '&:hover': { color: copied ? '#2e7d32' : PRIMARY, backgroundColor: `${PRIMARY}10` },
                            transition: 'color 0.2s',
                        }}
                    >
                        {copied ? <DoneIcon sx={{ fontSize: 17 }} /> : <ContentCopyIcon sx={{ fontSize: 17 }} />}
                    </IconButton>
                </Tooltip>
            </Box>

            <Box sx={{ px: 2, pb: 2 }}>
                {version.subtitle && (
                    <Typography sx={{
                        fontSize: '0.95rem', color: '#555',
                        fontWeight: 400, mb: 1.5, mt: 0.3, lineHeight: 1.5,
                    }}>
                        {version.subtitle}
                    </Typography>
                )}

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.2, mb: 2 }}>
                    {version.author && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, color: '#777' }}>
                            <PersonOutlineIcon sx={{ fontSize: 14 }} />
                            <Typography sx={{ fontSize: '0.76rem' }}>{version.author}</Typography>
                        </Box>
                    )}
                    {version.written_at && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, color: '#777' }}>
                            <CalendarTodayOutlinedIcon sx={{ fontSize: 13 }} />
                            <Typography sx={{ fontSize: '0.76rem' }}>
                                {formatDate(version.written_at)}
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Box sx={{
                    fontSize: '0.92rem',
                    color: '#2a2a2a',
                    lineHeight: 1.8,
                    '& p': { mb: 1.5 },
                    '& h1, & h2, & h3': { fontWeight: 700, mt: 2, mb: 1, color: '#1a1a1a' },
                    '& strong': { fontWeight: 600 },
                    '& em': { fontStyle: 'italic' },
                    '& ul, & ol': { pl: 2.5, mb: 1.5 },
                    '& li': { mb: 0.5 },
                    '& blockquote': {
                        borderLeft: `3px solid ${PRIMARY}`,
                        pl: 2, ml: 0, color: '#555', fontStyle: 'italic',
                    },
                }}>
                    <ReactMarkdown>
                        {version.article ?? ''}
                    </ReactMarkdown>
                </Box>

                <NeoFeedSourcesAudit sources={version.neofeed_sources} />

                <Box sx={{ mt: 2.5, pt: 1.5, borderTop: '1px solid #f5f5f5' }}>
                    <Typography sx={{ fontSize: '0.68rem', color: '#ccc' }}>
                        {getVersionKey(version) != null && `Versão #${getVersionKey(version)}`}
                        {getVersionKey(version) != null && version.response_id && ' · '}
                        {version.response_id && `Sessão: ${version.response_id}`}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}
